import React from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';
import { dateOfBirth21Validator } from '../../../util/dateOfBirth';
import approvedPrimaryButtonCss from '../../../styles/approvedPrimaryButton.module.css';

import {
  Form,
  FieldPasswordInput,
  PrimaryButton,
  FieldTextInput,
  FieldDateOfBirth,
  CustomExtendedDataField,
  ImageFromFile,
} from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';

import css from './SignupForm.module.css';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const isPasswordUsedMoreThanOnce = formValues => {
  const pw = formValues.password;
  const hasPasswordString = pw != null && pw.length >= validators.PASSWORD_MIN_LENGTH;

  if (hasPasswordString) {
    const isPasswordRepeated = Object.values(formValues).filter(v => v === pw).length > 1;
    return isPasswordRepeated;
  }
  return false;
};

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    initialValues={{ userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) }}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        preselectedUserType,
        userTypes,
        userFields,
        values,
      } = formRenderProps;

      const { userType } = values || {};

      const isProvider = userType === 'provider';
      const profileImageRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.profileImageRequired',
        })
      );

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );

      // password
      const passwordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooShort',
        },
        {
          minLength: validators.PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooLong',
        },
        {
          maxLength: validators.PASSWORD_MAX_LENGTH,
        }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      // Custom user fields. Since user types are not supported here,
      // only fields with no user type id limitation are selected.
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, userType);
      const accountFieldKeys = ['notificationChannel', 'opportunityAlerts', 'dateOfBirth'];
      const accountFieldProps = userFieldProps.filter(({ fieldConfig }) =>
        accountFieldKeys.includes(fieldConfig.key)
      );
      const professionalFieldProps = userFieldProps.filter(
        ({ fieldConfig }) => !accountFieldKeys.includes(fieldConfig.key)
      );

      const noUserTypes = !userType && !(userTypes?.length > 0);
      const userTypeConfig = userTypes.find(config => config.userType === userType);
      const showDefaultUserFields = userType || noUserTypes;
      const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;
      const dateOfBirthError = intl.formatMessage({ id: 'SignupForm.dateOfBirthInvalid' });

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress || isPasswordUsedMoreThanOnce(values);

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldSelectUserType
            name="userType"
            userTypes={userTypes}
            hasExistingUserType={!!preselectedUserType}
            intl={intl}
          />

          {showDefaultUserFields ? (
            <div className={css.defaultUserFields}>
              <FieldTextInput
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                label={intl.formatMessage({
                  id: 'SignupForm.emailLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.emailPlaceholder',
                })}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />
              <div className={css.name}>
                <FieldTextInput
                  className={css.firstNameRoot}
                  type="text"
                  id={formId ? `${formId}.fname` : 'fname'}
                  name="fname"
                  autoComplete="given-name"
                  label={intl.formatMessage({
                    id: 'SignupForm.firstNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.firstNamePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.firstNameRequired',
                    })
                  )}
                />
                <FieldTextInput
                  className={css.lastNameRoot}
                  type="text"
                  id={formId ? `${formId}.lname` : 'lname'}
                  name="lname"
                  autoComplete="family-name"
                  label={intl.formatMessage({
                    id: 'SignupForm.lastNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.lastNamePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.lastNameRequired',
                    })
                  )}
                />
              </div>

              <UserFieldDisplayName
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />

              {isProvider ? (
                <Field name="profileImage" validate={profileImageRequired}>
                  {({ input, meta }) => {
                    const imageId = formId ? `${formId}.profileImage` : 'profileImage';
                    const selectedFile = input.value;
                    const hasError = meta.touched && meta.error;

                    return (
                      <div className={css.profileImageField}>
                        <label className={css.profileImageLabel} htmlFor={imageId}>
                          {intl.formatMessage({ id: 'SignupForm.profileImageLabel' })}
                        </label>

                        {selectedFile ? (
                          <ImageFromFile
                            key={`${selectedFile.name}.${selectedFile.lastModified}.${selectedFile.size}`}
                            id={`${imageId}.preview.${selectedFile.name}.${selectedFile.lastModified}`}
                            rootClassName={css.profileImagePreviewRoot}
                            aspectWidth={1}
                            aspectHeight={1}
                            file={selectedFile}
                          />
                        ) : null}

                        <label className={css.profileImageSelect} htmlFor={imageId}>
                          {intl.formatMessage({
                            id: selectedFile
                              ? 'SignupForm.profileImageChange'
                              : 'SignupForm.profileImageSelect',
                          })}
                        </label>

                        <input
                          accept="image/*"
                          id={imageId}
                          name={input.name}
                          className={css.profileImageInput}
                          type="file"
                          onChange={event => {
                            const file = event.target.files?.[0] || null;
                            input.onChange(file);
                            input.onBlur();
                            event.target.value = '';
                          }}
                        />

                        <div className={css.profileImageHelp}>
                          {intl.formatMessage({ id: 'SignupForm.profileImageHelp' })}
                        </div>

                        {hasError ? <div className={css.error}>{meta.error}</div> : null}
                      </div>
                    );
                  }}
                </Field>
              ) : null}
              <FieldPasswordInput
                className={css.password}
                type="password"
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="new-password"
                label={intl.formatMessage({
                  id: 'SignupForm.passwordLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.passwordPlaceholder',
                })}
                validate={passwordValidators}
              />

              <UserFieldPhoneNumber
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />
              {accountFieldProps.map(({ key, ...fieldProps }) =>
                fieldProps.fieldConfig.key === 'dateOfBirth' ? (
                  <FieldDateOfBirth
                    key={key}
                    id={formId ? `${formId}.${key}` : key}
                    name={key}
                    validate={dateOfBirth21Validator(dateOfBirthError)}
                  />
                ) : (
                  <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
                )
              )}
            </div>
          ) : null}

          {showCustomUserFields ? (
            <div className={css.customFields}>
              {professionalFieldProps.map(({ key, ...fieldProps }) => (
                <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
              ))}
            </div>
          ) : null}

          <div className={css.bottomWrapper}>
            {termsAndConditions}
            {isPasswordUsedMoreThanOnce(values) ? (
              <div className={css.error}>
                <FormattedMessage id="SignupForm.passwordRepeatedOnOtherFields" />
              </div>
            ) : null}
            <PrimaryButton
              className={approvedPrimaryButtonCss.approvedPrimaryButton}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
            >
              <FormattedMessage id="SignupForm.signUp" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

/**
 * A component that renders the signup form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {ReactNode} props.termsAndConditions - The terms and conditions
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const SignupForm = props => {
  const intl = useIntl();
  return <SignupFormComponent {...props} intl={intl} />;
};

export default SignupForm;
